import type { Permission } from './Permission'

export enum UserPermissionDomain {
  // Domínios principais da interface
  DASHBOARD = 'dashboard',
  SALES = 'sales',
  CUSTOMERS = 'customers',
  STOCK = 'stock',
  PRODUCTS = 'products',
  FINANCIAL = 'financial',
  REPORTS = 'reports',
  SETTINGS = 'settings',
  INTEGRATIONS = 'integrations',
  POINT_OF_SALE = 'point_of_sale',
  TABLE_SERVICE = 'table_service',
  UNKNOWN = 'unknown'
}

export enum UserPermissionSubDomain {
  DASHBOARD = 'dashboard',
  BULK_PROCESS = 'bulk_process',
  STATISTICS = 'statistics',
  DRE = 'dre',
  CUSTOMER = 'customer',
  MARKETING = 'marketing',
  WHITELABEL = 'whitelabel',
  USER = 'user',
  PROFILE = 'profile',
  COMPANY = 'company',
  UNIT = 'unit',
  CUSTOMIZATION = 'customization',
  ICON = 'icon',
  LABEL = 'label',
  GRUPO_ICMS = 'grupo_icms',
  GRUPO_IPI = 'grupo_ipi',
  GRUPO_PIS_COFINS = 'grupo_pis_cofins',
  NFE = 'nfe',
  LEGAL_TERM = 'legal_term',
  ZIP_CODE = 'zip_code',
  INTEGRATION = 'integration',
  APP = 'app',
  POS = 'pos',
  PERIPHERAL = 'peripheral',
  KDS = 'kds',
  COMMAND_SERIES = 'command_series',
  PRODUCT = 'product',
  CATEGORY = 'category',
  PRICE_LIST = 'price_list',
  PROMOTION = 'promotion',
  STOCK = 'stock',
  PRODUCTION_ORDERS = 'production_orders',
  PURCHASE = 'purchase',
  SUPPLIER = 'supplier',
  FINANCIAL_TRANSACTION = 'financial_transaction',
  PAYMENT = 'payment',
  COST_CENTER = 'cost_center',
  RECONCILIATION = 'reconciliation',
  ACCOUNT_PLAN = 'account_plan',
  BANK = 'bank',
  DIGITAL_ACCOUNT = 'digital_account',
  CARD = 'card',
  CASH_REGISTER = 'cash_register',
  REPORT_SALE = 'report_sale',
  REPORT_PURCHASE = 'report_purchase',
  DELIVERY_AREA = 'delivery_area',
  CARRIER = 'carrier',
  OPENING_HOUR = 'opening_hour',
  TABLE = 'table',
  SETTINGS = 'settings',
  ERP_SETTINGS = 'erp_settings',
  TERMINAL_SETTINGS = 'terminal_settings',
  TOTEM_SETTINGS = 'totem_settings',
  SALES = 'sales',
  ORDER = 'order',
  CUSTOMER_CATEGORY = 'customer_category',
}

// Mapeamento de domínio principal -> subdomínios
export const USER_PERMISSION_DOMAIN_MAPPING = {
  [UserPermissionDomain.DASHBOARD]: [UserPermissionSubDomain.DASHBOARD, UserPermissionSubDomain.STATISTICS, UserPermissionSubDomain.DRE],
  [UserPermissionDomain.SALES]: [UserPermissionSubDomain.SALES, UserPermissionSubDomain.ORDER, UserPermissionSubDomain.PROMOTION, UserPermissionSubDomain.DELIVERY_AREA],
  [UserPermissionDomain.CUSTOMERS]: [UserPermissionSubDomain.CUSTOMER, UserPermissionSubDomain.MARKETING, UserPermissionSubDomain.WHITELABEL],
  [UserPermissionDomain.STOCK]: [UserPermissionSubDomain.STOCK, UserPermissionSubDomain.PRODUCTION_ORDERS, UserPermissionSubDomain.PURCHASE, UserPermissionSubDomain.SUPPLIER, UserPermissionSubDomain.CARRIER],
  [UserPermissionDomain.PRODUCTS]: [UserPermissionSubDomain.PRODUCT, UserPermissionSubDomain.CATEGORY, UserPermissionSubDomain.PRICE_LIST, UserPermissionSubDomain.BULK_PROCESS],
  [UserPermissionDomain.FINANCIAL]: [UserPermissionSubDomain.FINANCIAL_TRANSACTION, UserPermissionSubDomain.PAYMENT, UserPermissionSubDomain.COST_CENTER, UserPermissionSubDomain.RECONCILIATION, UserPermissionSubDomain.ACCOUNT_PLAN, UserPermissionSubDomain.BANK, UserPermissionSubDomain.DIGITAL_ACCOUNT, UserPermissionSubDomain.CARD, UserPermissionSubDomain.CASH_REGISTER],
  [UserPermissionDomain.REPORTS]: [UserPermissionSubDomain.REPORT_SALE, UserPermissionSubDomain.REPORT_PURCHASE],
  [UserPermissionDomain.SETTINGS]: [UserPermissionSubDomain.SETTINGS, UserPermissionSubDomain.ERP_SETTINGS, UserPermissionSubDomain.TERMINAL_SETTINGS, UserPermissionSubDomain.TOTEM_SETTINGS, UserPermissionSubDomain.USER, UserPermissionSubDomain.PROFILE, UserPermissionSubDomain.COMPANY, UserPermissionSubDomain.UNIT, UserPermissionSubDomain.CUSTOMIZATION, UserPermissionSubDomain.ICON, UserPermissionSubDomain.LABEL, UserPermissionSubDomain.GRUPO_ICMS, UserPermissionSubDomain.GRUPO_IPI, UserPermissionSubDomain.GRUPO_PIS_COFINS, UserPermissionSubDomain.NFE, UserPermissionSubDomain.LEGAL_TERM, UserPermissionSubDomain.ZIP_CODE, UserPermissionSubDomain.OPENING_HOUR],
  [UserPermissionDomain.INTEGRATIONS]: [UserPermissionSubDomain.INTEGRATION, UserPermissionSubDomain.APP],
  [UserPermissionDomain.POINT_OF_SALE]: [UserPermissionSubDomain.POS, UserPermissionSubDomain.PERIPHERAL, UserPermissionSubDomain.KDS, UserPermissionSubDomain.COMMAND_SERIES],
  [UserPermissionDomain.TABLE_SERVICE]: [UserPermissionSubDomain.TABLE]
}

export enum UserPermissions {
  // Address & Location
  READ_ZIP_CODE = 'read_zip_code',
  CREATE_ZIP_CODE = 'create_zip_code',
  UPDATE_ZIP_CODE = 'update_zip_code',
  DELETE_ZIP_CODE = 'delete_zip_code',

  // Whitelabel
  READ_WHITELABEL = 'read_whitelabel',
  CREATE_WHITELABEL = 'create_whitelabel',
  UPDATE_WHITELABEL = 'update_whitelabel',
  DELETE_WHITELABEL = 'delete_whitelabel',

  // User Management
  READ_USER = 'read_user',
  CREATE_USER = 'create_user',
  UPDATE_USER = 'update_user',
  DELETE_USER = 'delete_user',
  READ_PROFILE = 'read_profile',
  CREATE_PROFILE = 'create_profile',
  UPDATE_PROFILE = 'update_profile',
  DELETE_PROFILE = 'delete_profile',

  // Company & Units
  READ_COMPANY = 'read_company',
  CREATE_COMPANY = 'create_company',
  UPDATE_COMPANY = 'update_company',
  DELETE_COMPANY = 'delete_company',
  READ_UNIT = 'read_unit',
  CREATE_UNIT = 'create_unit',
  UPDATE_UNIT = 'update_unit',
  DELETE_UNIT = 'delete_unit',

  // Settings & Configuration
  READ_ERP_SETTINGS = 'read_erp_settings',
  CREATE_ERP_SETTINGS = 'create_erp_settings',
  UPDATE_ERP_SETTINGS = 'update_erp_settings',
  DELETE_ERP_SETTINGS = 'delete_erp_settings',
  READ_TERMINAL_SETTINGS = 'read_terminal_settings',
  CREATE_TERMINAL_SETTINGS = 'create_terminal_settings',
  UPDATE_TERMINAL_SETTINGS = 'update_terminal_settings',
  DELETE_TERMINAL_SETTINGS = 'delete_terminal_settings',
  READ_TOTEM_SETTINGS = 'read_totem_settings',
  CREATE_TOTEM_SETTINGS = 'create_totem_settings',
  UPDATE_TOTEM_SETTINGS = 'update_totem_settings',
  DELETE_TOTEM_SETTINGS = 'delete_totem_settings',

  // Physical Operations
  READ_TABLE = 'read_table',
  CREATE_TABLE = 'create_table',
  UPDATE_TABLE = 'update_table',
  DELETE_TABLE = 'delete_table',
  READ_KDS = 'read_kds',
  CREATE_KDS = 'create_kds',
  UPDATE_KDS = 'update_kds',
  DELETE_KDS = 'delete_kds',
  READ_POS = 'read_pos',
  CREATE_POS = 'create_pos',
  UPDATE_POS = 'update_pos',
  DELETE_POS = 'delete_pos',
  READ_PERIPHERAL = 'read_peripheral',
  CREATE_PERIPHERAL = 'create_peripheral',
  UPDATE_PERIPHERAL = 'update_peripheral',
  DELETE_PERIPHERAL = 'delete_peripheral',
  READ_COMMAND_SERIES = 'read_command_series',
  CREATE_COMMAND_SERIES = 'create_command_series',
  UPDATE_COMMAND_SERIES = 'update_command_series',
  DELETE_COMMAND_SERIES = 'delete_command_series',

  // Product & Catalog
  READ_PRODUCT = 'read_product',
  CREATE_PRODUCT = 'create_product',
  UPDATE_PRODUCT = 'update_product',
  DELETE_PRODUCT = 'delete_product',
  READ_CATEGORY = 'read_category',
  CREATE_CATEGORY = 'create_category',
  UPDATE_CATEGORY = 'update_category',
  DELETE_CATEGORY = 'delete_category',
  READ_PRICE_LIST = 'read_price_list',
  CREATE_PRICE_LIST = 'create_price_list',
  UPDATE_PRICE_LIST = 'update_price_list',
  DELETE_PRICE_LIST = 'delete_price_list',
  READ_PROMOTION = 'read_promotion',
  CREATE_PROMOTION = 'create_promotion',
  UPDATE_PROMOTION = 'update_promotion',
  DELETE_PROMOTION = 'delete_promotion',

  // Stock Management
  READ_STOCK = 'read_stock',
  CREATE_STOCK = 'create_stock',
  UPDATE_STOCK = 'update_stock',
  DELETE_STOCK = 'delete_stock',
  READ_STOCK_MOVEMENT = 'read_stock_movement',
  CREATE_STOCK_MOVEMENT = 'create_stock_movement',
  UPDATE_STOCK_MOVEMENT = 'update_stock_movement',
  DELETE_STOCK_MOVEMENT = 'delete_stock_movement',
  READ_STOCK_MOVEMENT_CATEGORY = 'read_stock_movement_category',
  CREATE_STOCK_MOVEMENT_CATEGORY = 'create_stock_movement_category',
  UPDATE_STOCK_MOVEMENT_CATEGORY = 'update_stock_movement_category',
  DELETE_STOCK_MOVEMENT_CATEGORY = 'delete_stock_movement_category',
  READ_PRODUCTION_ORDERS = 'read_production_orders',
  CREATE_PRODUCTION_ORDERS = 'create_production_orders',
  UPDATE_PRODUCTION_ORDERS = 'update_production_orders',
  DELETE_PRODUCTION_ORDERS = 'delete_production_orders',

  // Sales & Orders
  READ_SALE = 'read_sale',
  CREATE_SALE = 'create_sale',
  UPDATE_SALE = 'update_sale',
  DELETE_SALE = 'delete_sale',
  READ_SALE_ORDER = 'read_sale_order',
  CREATE_SALE_ORDER = 'create_sale_order',
  UPDATE_SALE_ORDER = 'update_sale_order',
  DELETE_SALE_ORDER = 'delete_sale_order',

  // Purchase Management
  READ_PURCHASE = 'read_purchase',
  CREATE_PURCHASE = 'create_purchase',
  UPDATE_PURCHASE = 'update_purchase',
  DELETE_PURCHASE = 'delete_purchase',
  READ_PURCHASE_INVOICE = 'read_purchase_invoice',
  CREATE_PURCHASE_INVOICE = 'create_purchase_invoice',
  UPDATE_PURCHASE_INVOICE = 'update_purchase_invoice',
  DELETE_PURCHASE_INVOICE = 'delete_purchase_invoice',
  READ_SUPPLIER = 'read_supplier',
  CREATE_SUPPLIER = 'create_supplier',
  UPDATE_SUPPLIER = 'update_supplier',
  DELETE_SUPPLIER = 'delete_supplier',

  // Customer Management
  READ_CUSTOMER = 'read_customer',
  CREATE_CUSTOMER = 'create_customer',
  UPDATE_CUSTOMER = 'update_customer',
  DELETE_CUSTOMER = 'delete_customer',
  READ_CUSTOMER_CATEGORY = 'read_customer_category',
  CREATE_CUSTOMER_CATEGORY = 'create_customer_category',
  UPDATE_CUSTOMER_CATEGORY = 'update_customer_category',
  DELETE_CUSTOMER_CATEGORY = 'delete_customer_category',

  // Financial Management
  READ_FINANCIAL_TRANSACTION = 'read_financial_transaction',
  CREATE_FINANCIAL_TRANSACTION = 'create_financial_transaction',
  UPDATE_FINANCIAL_TRANSACTION = 'update_financial_transaction',
  DELETE_FINANCIAL_TRANSACTION = 'delete_financial_transaction',
  READ_PAYMENT = 'read_payment',
  CREATE_PAYMENT = 'create_payment',
  UPDATE_PAYMENT = 'update_payment',
  DELETE_PAYMENT = 'delete_payment',
  READ_COST_CENTER = 'read_cost_center',
  CREATE_COST_CENTER = 'create_cost_center',
  UPDATE_COST_CENTER = 'update_cost_center',
  DELETE_COST_CENTER = 'delete_cost_center',
  READ_RECONCILIATION = 'read_reconciliation',
  CREATE_RECONCILIATION = 'create_reconciliation',
  UPDATE_RECONCILIATION = 'update_reconciliation',
  DELETE_RECONCILIATION = 'delete_reconciliation',
  READ_ACCOUNT_PLAN = 'read_account_plan',
  CREATE_ACCOUNT_PLAN = 'create_account_plan',
  UPDATE_ACCOUNT_PLAN = 'update_account_plan',
  DELETE_ACCOUNT_PLAN = 'delete_account_plan',

  // Banking
  READ_BANK = 'read_bank',
  CREATE_BANK = 'create_bank',
  UPDATE_BANK = 'update_bank',
  DELETE_BANK = 'delete_bank',
  READ_BANK_ACCOUNT = 'read_bank_account',
  READ_BANK_ACCOUNT_BALANCE = 'read_bank_account_balance',
  CREATE_BANK_ACCOUNT = 'create_bank_account',
  UPDATE_BANK_ACCOUNT = 'update_bank_account',
  DELETE_BANK_ACCOUNT = 'delete_bank_account',
  READ_DIGITAL_ACCOUNT = 'read_digital_account',
  CREATE_DIGITAL_ACCOUNT = 'create_digital_account',
  UPDATE_DIGITAL_ACCOUNT = 'update_digital_account',
  DELETE_DIGITAL_ACCOUNT = 'delete_digital_account',

  // Cash & Cards
  READ_CASH_REGISTER = 'read_cash_register',
  CREATE_CASH_REGISTER = 'create_cash_register',
  UPDATE_CASH_REGISTER = 'update_cash_register',
  DELETE_CASH_REGISTER = 'delete_cash_register',
  READ_CARD = 'read_card',
  CREATE_CARD = 'create_card',
  UPDATE_CARD = 'update_card',
  DELETE_CARD = 'delete_card',
  READ_CARD_CONTRACT = 'read_card_contract',
  CREATE_CARD_CONTRACT = 'create_card_contract',
  UPDATE_CARD_CONTRACT = 'update_card_contract',
  DELETE_CARD_CONTRACT = 'delete_card_contract',

  // Tax Management
  READ_GRUPO_ICMS = 'read_grupo_icms',
  CREATE_GRUPO_ICMS = 'create_grupo_icms',
  UPDATE_GRUPO_ICMS = 'update_grupo_icms',
  DELETE_GRUPO_ICMS = 'delete_grupo_icms',
  READ_GRUPO_IPI = 'read_grupo_ipi',
  CREATE_GRUPO_IPI = 'create_grupo_ipi',
  UPDATE_GRUPO_IPI = 'update_grupo_ipi',
  DELETE_GRUPO_IPI = 'delete_grupo_ipi',
  READ_GRUPO_PIS_COFINS = 'read_grupo_pis_cofins',
  CREATE_GRUPO_PIS_COFINS = 'create_grupo_pis_cofins',
  UPDATE_GRUPO_PIS_COFINS = 'update_grupo_pis_cofins',
  DELETE_GRUPO_PIS_COFINS = 'delete_grupo_pis_cofins',

  // NFe & Legal
  READ_NFE = 'read_nfe',
  UPDATE_NFE = 'update_nfe',
  READ_CONFIG_NFE = 'read_config_nfe',
  UPDATE_CONFIG_NFE = 'update_config_nfe',
  READ_NFE_RECEIVED_DOCUMENTS = 'read_nfe_received_documents',
  UPDATE_NFE_RECEIVED_DOCUMENTS = 'update_nfe_received_documents',
  READ_NFE_EMITTED_DOCUMENT_SCHEDULE = 'read_nfe_emitted_document_schedule',
  CREATE_NFE_EMITTED_DOCUMENT_SCHEDULE = 'create_nfe_emitted_document_schedule',
  DELETE_NFE_EMITTED_DOCUMENT_SCHEDULE = 'delete_nfe_emitted_document_schedule',
  READ_ADDITIONAL_INFO_NFE = 'read_additional_info_nfe',
  CREATE_ADDITIONAL_INFO_NFE = 'create_additional_info_nfe',
  UPDATE_ADDITIONAL_INFO_NFE = 'update_additional_info_nfe',
  DELETE_ADDITIONAL_INFO_NFE = 'delete_additional_info_nfe',
  READ_STANDARD_OBSERVATION = 'read_standard_observation',
  CREATE_STANDARD_OBSERVATION = 'create_standard_observation',
  UPDATE_STANDARD_OBSERVATION = 'update_standard_observation',
  DELETE_STANDARD_OBSERVATION = 'delete_standard_observation',
  READ_NATURE_OPERATION = 'read_nature_operation',
  CREATE_NATURE_OPERATION = 'create_nature_operation',
  UPDATE_NATURE_OPERATION = 'update_nature_operation',
  DELETE_NATURE_OPERATION = 'delete_nature_operation',
  READ_INVOICE_SERIE = 'read_invoice_serie',
  CREATE_INVOICE_SERIE = 'create_invoice_serie',
  UPDATE_INVOICE_SERIE = 'update_invoice_serie',
  DELETE_INVOICE_SERIE = 'delete_invoice_serie',
  READ_TYPE_INVOICE_PDV = 'read_type_invoice_pdv',
  UPDATE_TYPE_INVOICE_PDV = 'update_type_invoice_pdv',
  READ_TAX_SETTINGS_DEFAULTS = 'read_tax_settings_defaults',
  UPDATE_TAX_SETTINGS_DEFAULTS = 'update_tax_settings_defaults',
  READ_ISSQN = 'read_issqn',
  CREATE_ISSQN = 'create_issqn',
  UPDATE_ISSQN = 'update_issqn',
  DELETE_ISSQN = 'delete_issqn',
  READ_CERTIFICATE_TICKET = 'read_certificate_ticket',
  CREATE_CERTIFICATE_TICKET = 'create_certificate_ticket',
  UPDATE_CERTIFICATE_TICKET = 'update_certificate_ticket',
  DELETE_CERTIFICATE_TICKET = 'delete_certificate_ticket',
  READ_LEGAL_TERM = 'read_legal_term',
  CREATE_LEGAL_TERM = 'create_legal_term',
  UPDATE_LEGAL_TERM = 'update_legal_term',
  DELETE_LEGAL_TERM = 'delete_legal_term',

  // Reports & Statistics
  READ_REPORT_SALE = 'read_report_sale',
  READ_REPORT_PURCHASE = 'read_report_purchase',
  READ_REPORT_V3 = 'read_report_v3',
  WRITE_REPORT_V3 = 'write_report_v3',
  READ_STATISTICS = 'read_statistics',
  CREATE_STATISTICS = 'create_statistics',
  UPDATE_STATISTICS = 'update_statistics',
  DELETE_STATISTICS = 'delete_statistics',
  READ_DRE = 'read_dre',

  // Apps & Integrations
  READ_APP = 'read_app',
  CREATE_APP = 'create_app',
  UPDATE_APP = 'update_app',
  DELETE_APP = 'delete_app',
  ATTACH_PAYMENT_APP = 'attach_payment_app',
  DETACH_PAYMENT_APP = 'detach_payment_app',
  CREATE_TOKEN_FIREBASE = 'create_token_firebase',
  READ_INTEGRATION = 'read_integration',
  UPDATE_INTEGRATION = 'update_integration',
  DELETE_INTEGRATION = 'delete_integration',

  // Delivery & Logistics
  READ_DELIVERY_AREA = 'read_delivery_area',
  CREATE_DELIVERY_AREA = 'create_delivery_area',
  UPDATE_DELIVERY_AREA = 'update_delivery_area',
  DELETE_DELIVERY_AREA = 'delete_delivery_area',
  READ_CARRIER = 'read_carrier',
  CREATE_CARRIER = 'create_carrier',
  UPDATE_CARRIER = 'update_carrier',
  DELETE_CARRIER = 'delete_carrier',
  READ_OPENING_HOUR = 'read_opening_hour',
  CREATE_OPENING_HOUR = 'create_opening_hour',
  UPDATE_OPENING_HOUR = 'update_opening_hour',
  DELETE_OPENING_HOUR = 'delete_opening_hour',

  // Customization & Content
  READ_CUSTOMIZATION = 'read_customization',
  CREATE_CUSTOMIZATION = 'create_customization',
  UPDATE_CUSTOMIZATION = 'update_customization',
  DELETE_CUSTOMIZATION = 'delete_customization',
  READ_ICON = 'read_icon',
  CREATE_ICON = 'create_icon',
  UPDATE_ICON = 'update_icon',
  DELETE_ICON = 'delete_icon',
  READ_LABEL = 'read_label',
  CREATE_LABEL = 'create_label',
  UPDATE_LABEL = 'update_label',
  DELETE_LABEL = 'delete_label',

  // System & Utilities
  READ_STATUS = 'read_status',
  CREATE_STATUS = 'create_status',
  UPDATE_STATUS = 'update_status',
  DELETE_STATUS = 'delete_status',
  READ_SOURCE = 'read_source',
  CREATE_SOURCE = 'create_source',
  UPDATE_SOURCE = 'update_source',
  DELETE_SOURCE = 'delete_source',
  READ_FILTER = 'read_filter',
  CREATE_FILTER = 'create_filter',
  UPDATE_FILTER = 'update_filter',
  DELETE_FILTER = 'delete_filter',
  READ_FILE = 'read_file',
  READ_SURVEY = 'read_survey',
  CREATE_SURVEY = 'create_survey',
  UPDATE_SURVEY = 'update_survey',
  DELETE_SURVEY = 'delete_survey',

  // Bulk Operations
  READ_BULK_PROCESS = 'read_bulkProcess',
  CREATE_BULK_PROCESS = 'create_bulkProcess',
  UPDATE_BULK_PROCESS = 'update_bulkProcess',
  DELETE_BULK_PROCESS = 'delete_bulkProcess',

  // Plans & Billing
  READ_PROPOSAL = 'read_proposal',
  CREATE_PROPOSAL = 'create_proposal',
  UPDATE_PROPOSAL = 'update_proposal',
  DELETE_PROPOSAL = 'delete_proposal',
  READ_BILL = 'read_bill',
  CREATE_BILL = 'create_bill',
  UPDATE_BILL = 'update_bill',
  DELETE_BILL = 'delete_bill',
  READ_PLAN = 'read_plan',
  CREATE_PLAN = 'create_plan',
  UPDATE_PLAN = 'update_plan',
  DELETE_PLAN = 'delete_plan',
  READ_CANCEL_REQUEST = 'read_cancel_request',
  CREATE_CANCEL_REQUEST = 'create_cancel_request',
  UPDATE_CANCEL_REQUEST = 'update_cancel_request',
  DELETE_CANCEL_REQUEST = 'delete_cancel_request',

  // Training & Support
  READ_TRAINING = 'read_training',
  CREATE_TRAINING = 'create_training',
  DELETE_TRAINING = 'delete_training',

  // Utilities
  UPLOAD_TEMPORARY_IMAGE = 'upload_temporary_image',
  READ_DISTRICT = 'read_district',
  CREATE_DISTRICT = 'create_district',
  UPDATE_DISTRICT = 'update_district',
  DELETE_DISTRICT = 'delete_district',
  READ_PASSWORD_PANEL = 'read_password_panel',
  CREATE_PASSWORD_PANEL = 'create_password_panel',
  UPDATE_PASSWORD_PANEL = 'update_password_panel',
  DELETE_PASSWORD_PANEL = 'delete_password_panel',
}

// Labels dos domínios principais (em inglês para tradução na view)
export const USER_PERMISSION_DOMAIN_LABELS = {
  [UserPermissionDomain.DASHBOARD]: 'Dashboard',
  [UserPermissionDomain.SALES]: 'Sales',
  [UserPermissionDomain.CUSTOMERS]: 'Customers',
  [UserPermissionDomain.STOCK]: 'Stock',
  [UserPermissionDomain.PRODUCTS]: 'Products',
  [UserPermissionDomain.FINANCIAL]: 'Financial',
  [UserPermissionDomain.REPORTS]: 'Reports',
  [UserPermissionDomain.SETTINGS]: 'Settings',
  [UserPermissionDomain.INTEGRATIONS]: 'Integrations',
  [UserPermissionDomain.POINT_OF_SALE]: 'Point of Sale',
  [UserPermissionDomain.TABLE_SERVICE]: 'Table Service',
  [UserPermissionDomain.UNKNOWN]: 'Others'
}

// Labels dos subdomínios (em inglês para tradução na view)
export const USER_PERMISSION_SUBDOMAIN_LABELS = {
  [UserPermissionSubDomain.BULK_PROCESS]: 'Bulk Process',
  [UserPermissionSubDomain.DASHBOARD]: 'Dashboard',
  [UserPermissionSubDomain.STATISTICS]: 'Statistics',
  [UserPermissionSubDomain.DRE]: 'DRE',
  [UserPermissionSubDomain.CUSTOMER]: 'Customers',
  [UserPermissionSubDomain.MARKETING]: 'Marketing',
  [UserPermissionSubDomain.WHITELABEL]: 'Whitelabel',
  [UserPermissionSubDomain.USER]: 'Users',
  [UserPermissionSubDomain.PROFILE]: 'Profiles',
  [UserPermissionSubDomain.COMPANY]: 'Companies',
  [UserPermissionSubDomain.UNIT]: 'Units',
  [UserPermissionSubDomain.CUSTOMIZATION]: 'Customization',
  [UserPermissionSubDomain.ICON]: 'Icons',
  [UserPermissionSubDomain.LABEL]: 'Labels',
  [UserPermissionSubDomain.GRUPO_ICMS]: 'ICMS Group',
  [UserPermissionSubDomain.GRUPO_IPI]: 'IPI Group',
  [UserPermissionSubDomain.GRUPO_PIS_COFINS]: 'PIS/COFINS Group',
  [UserPermissionSubDomain.NFE]: 'NFe',
  [UserPermissionSubDomain.LEGAL_TERM]: 'Legal Terms',
  [UserPermissionSubDomain.ZIP_CODE]: 'ZIP Code',
  [UserPermissionSubDomain.INTEGRATION]: 'Integrations',
  [UserPermissionSubDomain.APP]: 'Applications',
  [UserPermissionSubDomain.POS]: 'POS',
  [UserPermissionSubDomain.PERIPHERAL]: 'Peripherals',
  [UserPermissionSubDomain.KDS]: 'KDS',
  [UserPermissionSubDomain.COMMAND_SERIES]: 'Command Series',
  [UserPermissionSubDomain.PRODUCT]: 'Products',
  [UserPermissionSubDomain.CATEGORY]: 'Categories',
  [UserPermissionSubDomain.PRICE_LIST]: 'Price Lists',
  [UserPermissionSubDomain.PROMOTION]: 'Promotions',
  [UserPermissionSubDomain.STOCK]: 'Stock',
  [UserPermissionSubDomain.PRODUCTION_ORDERS]: 'Production Orders',
  [UserPermissionSubDomain.PURCHASE]: 'Purchases',
  [UserPermissionSubDomain.SUPPLIER]: 'Suppliers',
  [UserPermissionSubDomain.FINANCIAL_TRANSACTION]: 'Financial Transactions',
  [UserPermissionSubDomain.PAYMENT]: 'Payments',
  [UserPermissionSubDomain.COST_CENTER]: 'Cost Centers',
  [UserPermissionSubDomain.RECONCILIATION]: 'Reconciliation',
  [UserPermissionSubDomain.ACCOUNT_PLAN]: 'Account Plan',
  [UserPermissionSubDomain.BANK]: 'Banks',
  [UserPermissionSubDomain.DIGITAL_ACCOUNT]: 'Digital Accounts',
  [UserPermissionSubDomain.CARD]: 'Cards',
  [UserPermissionSubDomain.CASH_REGISTER]: 'Cash Register',
  [UserPermissionSubDomain.REPORT_SALE]: 'Sales Reports',
  [UserPermissionSubDomain.REPORT_PURCHASE]: 'Purchase Reports',
  [UserPermissionSubDomain.DELIVERY_AREA]: 'Delivery Areas',
  [UserPermissionSubDomain.CARRIER]: 'Carriers',
  [UserPermissionSubDomain.OPENING_HOUR]: 'Opening Hours',
  [UserPermissionSubDomain.TABLE]: 'Tables',
  [UserPermissionSubDomain.SETTINGS]: 'Settings',
  [UserPermissionSubDomain.ERP_SETTINGS]: 'ERP Settings',
  [UserPermissionSubDomain.TERMINAL_SETTINGS]: 'Terminal Settings',
  [UserPermissionSubDomain.TOTEM_SETTINGS]: 'Totem Settings',
  [UserPermissionSubDomain.SALES]: 'Sales',
  [UserPermissionSubDomain.ORDER]: 'Orders',
  [UserPermissionSubDomain.CUSTOMER_CATEGORY]: 'Customer Categories'
}

export interface UserPermission extends Permission<UserPermissionDomain, UserPermissionSubDomain> {}

export interface PermissionSubGroup {
  subDomain: UserPermissionSubDomain
  label: string
  permissions: UserPermission[]
}

export interface PermissionGroup {
  domain: UserPermissionDomain
  label: string
  permissions: UserPermission[]
  subGroups?: PermissionSubGroup[]
}
